import { useEffect, useState } from "react";
import { Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react";
import { membershipApi } from "../api";
import { useAuth } from "#/context/AuthProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { useToast } from "#/components/common/ToastProvider";
import { getErrorMessage } from "#/features/leave/hooks";
import type { FeeFrequency, MembershipCategory, MembershipFeeType } from "../types";

// ── Primitive components ──────────────────────────────────────────────────────

const fieldCls =
  "w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-black uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function CBool({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-emerald-700"
      />
      <span className="text-sm font-bold text-slate-700">{label}</span>
    </label>
  );
}

// ── Category form state ───────────────────────────────────────────────────────

interface CatForm {
  code: string;
  name: string;
  description: string;
  auctionAccessAllowed: boolean;
  requiresBoardApproval: boolean;
  requiresPayment: boolean;
  requiresIctSetup: boolean;
  requiresTraining: boolean;
  active: boolean;
}

const EMPTY_CAT: CatForm = {
  code: "",
  name: "",
  description: "",
  auctionAccessAllowed: true,
  requiresBoardApproval: true,
  requiresPayment: true,
  requiresIctSetup: true,
  requiresTraining: true,
  active: true,
};

// ── Fee form state ────────────────────────────────────────────────────────────

interface FeeForm {
  code: string;
  name: string;
  description: string;
  frequency: FeeFrequency;
  appliesToAllCategories: boolean;
  amountVisible: boolean;
  defaultAmount: string;
  currency: string;
  active: boolean;
  categoryIds: number[];
}

const EMPTY_FEE: FeeForm = {
  code: "",
  name: "",
  description: "",
  frequency: "ANNUAL",
  appliesToAllCategories: true,
  amountVisible: true,
  defaultAmount: "",
  currency: "KES",
  active: true,
  categoryIds: [],
};

// ── Main component ────────────────────────────────────────────────────────────

export function MembershipConfigPanel() {
  const { hasAnyPermission, hasAnyRole } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();

  const canManage =
    hasAnyRole(["ADMIN", "SUPER_ADMIN"]) ||
    hasAnyPermission(["HR_MEMBERSHIP_CONFIG_MANAGE", "HR_MEMBERSHIP_MANAGE"]);

  const [categories, setCategories] = useState<MembershipCategory[]>([]);
  const [fees, setFees] = useState<MembershipFeeType[]>([]);
  const [loading, setLoading] = useState(true);

  // Category form
  const [showCatForm, setShowCatForm] = useState(false);
  const [editCatId, setEditCatId] = useState<number | null>(null);
  const [catForm, setCatForm] = useState<CatForm>(EMPTY_CAT);
  const [catSaving, setCatSaving] = useState(false);

  // Fee form
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [editFeeId, setEditFeeId] = useState<number | null>(null);
  const [feeForm, setFeeForm] = useState<FeeForm>(EMPTY_FEE);
  const [feeSaving, setFeeSaving] = useState(false);
  const [deletingFeeId, setDeletingFeeId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [catData, feeData] = await Promise.all([
        membershipApi.listCategories(),
        membershipApi.listFeeTypes(),
      ]);
      setCategories(catData);
      setFees(feeData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Category handlers ───────────────────────────────────────────────────────

  const openNewCat = () => {
    setEditCatId(null);
    setCatForm(EMPTY_CAT);
    setShowCatForm(true);
  };

  const openEditCat = (c: MembershipCategory) => {
    setEditCatId(c.id);
    setCatForm({
      code: c.code ?? "",
      name: c.name,
      description: c.description ?? "",
      auctionAccessAllowed: c.auctionAccessAllowed,
      requiresBoardApproval: c.requiresBoardApproval,
      requiresPayment: c.requiresPayment,
      requiresIctSetup: c.requiresIctSetup,
      requiresTraining: c.requiresTraining,
      active: c.active,
    });
    setShowCatForm(true);
  };

  const saveCat = async () => {
    setCatSaving(true);
    try {
      if (editCatId) {
        await membershipApi.updateCategory(editCatId, catForm);
      } else {
        await membershipApi.createCategory(catForm);
      }
      setShowCatForm(false);
      setEditCatId(null);
      setCatForm(EMPTY_CAT);
      await load();
    } finally {
      setCatSaving(false);
    }
  };

  // ── Fee handlers ────────────────────────────────────────────────────────────

  const openNewFee = () => {
    setEditFeeId(null);
    setFeeForm(EMPTY_FEE);
    setShowFeeForm(true);
  };

  const openEditFee = (f: MembershipFeeType) => {
    setEditFeeId(f.id);
    setFeeForm({
      code: f.code ?? "",
      name: f.name,
      description: f.description ?? "",
      frequency: f.frequency,
      appliesToAllCategories: f.appliesToAllCategories,
      amountVisible: f.amountVisible,
      defaultAmount: f.defaultAmount != null ? String(f.defaultAmount) : "",
      currency: f.currency ?? "KES",
      active: f.active,
      categoryIds: f.categoryIds ?? [],
    });
    setShowFeeForm(true);
  };

  const saveFee = async () => {
    setFeeSaving(true);
    try {
      const payload = {
        ...feeForm,
        defaultAmount: feeForm.defaultAmount ? Number(feeForm.defaultAmount) : undefined,
        categoryIds: feeForm.appliesToAllCategories ? [] : feeForm.categoryIds,
      };
      if (editFeeId) {
        await membershipApi.updateFeeType(editFeeId, payload);
      } else {
        await membershipApi.createFeeType(payload);
      }
      setShowFeeForm(false);
      setEditFeeId(null);
      setFeeForm(EMPTY_FEE);
      await load();
    } finally {
      setFeeSaving(false);
    }
  };

  const deleteFee = async (f: MembershipFeeType) => {
    if (!(await confirm(`Delete fee type "${f.name}"? This cannot be undone.`))) return;
    setDeletingFeeId(f.id);
    try {
      await membershipApi.deleteFeeType(f.id);
      toast.success("Fee type deleted.");
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeletingFeeId(null);
    }
  };

  const toggleFeeCategory = (id: number) => {
    setFeeForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(id)
        ? prev.categoryIds.filter((c) => c !== id)
        : [...prev.categoryIds, id],
    }));
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="grid gap-6 xl:grid-cols-2">

      {/* ── Categories ──────────────────────────────────────────────── */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-slate-900">Membership Categories</h2>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} className="rounded-2xl border border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50">
              <RefreshCw className="h-4 w-4" />
            </button>
            {canManage && (
              <button type="button" onClick={openNewCat} className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2.5 text-xs font-black text-white hover:bg-emerald-800">
                <Plus className="h-3.5 w-3.5" /> New Category
              </button>
            )}
          </div>
        </div>

        {/* Category form */}
        {showCatForm && canManage && (
          <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-black uppercase tracking-wide text-emerald-700">
                {editCatId ? "Edit Category" : "New Category"}
              </p>
              <button type="button" onClick={() => setShowCatForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Code *">
                <input value={catForm.code} onChange={(e) => setCatForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. BROKER" className={fieldCls} />
              </Field>
              <Field label="Name *">
                <input value={catForm.name} onChange={(e) => setCatForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Broker" className={fieldCls} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea value={catForm.description} onChange={(e) => setCatForm(p => ({ ...p, description: e.target.value }))} rows={2} className={fieldCls} />
                </Field>
              </div>
              <p className="text-xs font-black uppercase tracking-wide text-slate-500 md:col-span-2">Onboarding Requirements</p>
              <CBool label="Requires board approval" checked={catForm.requiresBoardApproval} onChange={(v) => setCatForm(p => ({ ...p, requiresBoardApproval: v }))} />
              <CBool label="Requires payment / subscription" checked={catForm.requiresPayment} onChange={(v) => setCatForm(p => ({ ...p, requiresPayment: v }))} />
              <CBool label="Requires ICT account setup" checked={catForm.requiresIctSetup} onChange={(v) => setCatForm(p => ({ ...p, requiresIctSetup: v }))} />
              <CBool label="Requires training" checked={catForm.requiresTraining} onChange={(v) => setCatForm(p => ({ ...p, requiresTraining: v }))} />
              <CBool label="Auction access allowed" checked={catForm.auctionAccessAllowed} onChange={(v) => setCatForm(p => ({ ...p, auctionAccessAllowed: v }))} />
              <CBool label="Active" checked={catForm.active} onChange={(v) => setCatForm(p => ({ ...p, active: v }))} />
            </div>
            <button type="button" onClick={saveCat} disabled={catSaving || !catForm.code || !catForm.name} className="mt-4 rounded-2xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60">
              {catSaving ? "Saving…" : editCatId ? "Update Category" : "Create Category"}
            </button>
          </div>
        )}

        {/* Category list */}
        {loading ? (
          <p className="text-sm text-slate-400 py-4">Loading…</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">Code</th>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Board</th>
                  <th className="px-3 py-3">Payment</th>
                  <th className="px-3 py-3">ICT</th>
                  <th className="px-3 py-3">Training</th>
                  <th className="px-3 py-3">Auction</th>
                  <th className="px-3 py-3">Status</th>
                  {canManage && <th className="px-3 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {categories.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3 font-mono text-xs font-bold">{c.code}</td>
                    <td className="px-3 py-3 font-bold text-slate-900 whitespace-nowrap">{c.name}</td>
                    <td className="px-3 py-3 text-center">{c.requiresBoardApproval ? <Tick /> : <Cross />}</td>
                    <td className="px-3 py-3 text-center">{c.requiresPayment ? <Tick /> : <Cross />}</td>
                    <td className="px-3 py-3 text-center">{c.requiresIctSetup ? <Tick /> : <Cross />}</td>
                    <td className="px-3 py-3 text-center">{c.requiresTraining ? <Tick /> : <Cross />}</td>
                    <td className="px-3 py-3 text-center">{c.auctionAccessAllowed ? <Tick /> : <Cross />}</td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {c.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-3 py-3">
                        <button type="button" onClick={() => openEditCat(c)} className="rounded-xl border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!categories.length && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No categories configured.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Fee Types ───────────────────────────────────────────────── */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-slate-900">Fees, Levies & Subscriptions</h2>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} className="rounded-2xl border border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50">
              <RefreshCw className="h-4 w-4" />
            </button>
            {canManage && (
              <button type="button" onClick={openNewFee} className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2.5 text-xs font-black text-white hover:bg-emerald-800">
                <Plus className="h-3.5 w-3.5" /> New Fee
              </button>
            )}
          </div>
        </div>

        {/* Fee form */}
        {showFeeForm && canManage && (
          <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-black uppercase tracking-wide text-emerald-700">
                {editFeeId ? "Edit Fee Type" : "New Fee Type"}
              </p>
              <button type="button" onClick={() => setShowFeeForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Code *">
                <input value={feeForm.code} onChange={(e) => setFeeForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. ANN_SUB" className={fieldCls} />
              </Field>
              <Field label="Name *">
                <input value={feeForm.name} onChange={(e) => setFeeForm(p => ({ ...p, name: e.target.value }))} placeholder="Annual Subscription" className={fieldCls} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea value={feeForm.description} onChange={(e) => setFeeForm(p => ({ ...p, description: e.target.value }))} rows={2} className={fieldCls} />
                </Field>
              </div>
              <Field label="Frequency">
                <select value={feeForm.frequency} onChange={(e) => setFeeForm(p => ({ ...p, frequency: e.target.value as FeeFrequency }))} className={fieldCls}>
                  <option value="ONE_TIME">One-time</option>
                  <option value="ANNUAL">Annual</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="HALF_YEARLY">Half-yearly</option>
                  <option value="ADHOC">Adhoc</option>
                </select>
              </Field>
              <Field label="Currency">
                <input value={feeForm.currency} onChange={(e) => setFeeForm(p => ({ ...p, currency: e.target.value }))} placeholder="KES" className={fieldCls} />
              </Field>
              <Field label="Default Amount">
                <input type="number" value={feeForm.defaultAmount} onChange={(e) => setFeeForm(p => ({ ...p, defaultAmount: e.target.value }))} placeholder="e.g. 50000" className={fieldCls} />
              </Field>
              <div className="flex flex-col gap-2 md:col-span-2">
                <CBool label="Amount visible to all users (uncheck to hide from non-finance)" checked={feeForm.amountVisible} onChange={(v) => setFeeForm(p => ({ ...p, amountVisible: v }))} />
                <CBool label="Active" checked={feeForm.active} onChange={(v) => setFeeForm(p => ({ ...p, active: v }))} />
                <CBool label="Applies to ALL membership categories" checked={feeForm.appliesToAllCategories} onChange={(v) => setFeeForm(p => ({ ...p, appliesToAllCategories: v, categoryIds: v ? [] : p.categoryIds }))} />
              </div>

              {/* Category assignment (shown only when NOT all categories) */}
              {!feeForm.appliesToAllCategories && (
                <div className="md:col-span-2">
                  <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">
                    Assign to specific categories
                  </p>
                  {categories.length === 0 ? (
                    <p className="text-xs text-slate-400">No categories loaded yet.</p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {categories.map((c) => (
                        <label key={c.id} className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-3 py-2.5 transition ${feeForm.categoryIds.includes(c.id) ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                          <input
                            type="checkbox"
                            checked={feeForm.categoryIds.includes(c.id)}
                            onChange={() => toggleFeeCategory(c.id)}
                            className="accent-emerald-700"
                          />
                          <div>
                            <span className="text-sm font-bold text-slate-700">{c.name}</span>
                            <span className="ml-1.5 font-mono text-xs text-slate-400">{c.code}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button type="button" onClick={saveFee} disabled={feeSaving || !feeForm.code || !feeForm.name} className="mt-4 rounded-2xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60">
              {feeSaving ? "Saving…" : editFeeId ? "Update Fee Type" : "Create Fee Type"}
            </button>
          </div>
        )}

        {/* Fee list */}
        {loading ? (
          <p className="text-sm text-slate-400 py-4">Loading…</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">Code</th>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Frequency</th>
                  <th className="px-3 py-3">Default Amount</th>
                  <th className="px-3 py-3">Applies To</th>
                  <th className="px-3 py-3">Visible</th>
                  <th className="px-3 py-3">Status</th>
                  {canManage && <th className="px-3 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fees.map((f) => {
                  const catNames = f.appliesToAllCategories
                    ? "All categories"
                    : (f.categoryIds ?? [])
                        .map((id) => categories.find((c) => c.id === id)?.name ?? id)
                        .join(", ") || "None selected";
                  return (
                    <tr key={f.id} className="hover:bg-slate-50">
                      <td className="px-3 py-3 font-mono text-xs font-bold">{f.code}</td>
                      <td className="px-3 py-3 font-bold text-slate-900 whitespace-nowrap">{f.name}</td>
                      <td className="px-3 py-3 text-slate-600">{f.frequency.replace(/_/g, " ")}</td>
                      <td className="px-3 py-3 text-slate-600">
                        {f.defaultAmount != null ? `${f.currency ?? "KES"} ${f.defaultAmount.toLocaleString()}` : "—"}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600 max-w-[180px]">
                        {f.appliesToAllCategories ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-700">All</span>
                        ) : (
                          <span className="text-slate-500">{catNames}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">{f.amountVisible ? <Tick /> : <Cross />}</td>
                      <td className="px-3 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${f.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {f.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <button type="button" onClick={() => openEditFee(f)} className="rounded-xl border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteFee(f)}
                              disabled={deletingFeeId === f.id}
                              className="rounded-xl border border-slate-200 p-1.5 text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!fees.length && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No fee types configured.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ── Tiny helpers ──────────────────────────────────────────────────────────────

function Tick() {
  return <span className="text-emerald-600 font-black text-sm">✓</span>;
}
function Cross() {
  return <span className="text-slate-300 text-sm">—</span>;
}
